import { useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { Float } from "@react-three/drei";
import * as THREE from "three";

// ---------------------------------------------------------------------------
// Unified 3D design system — every satellite scene shares the main property
// scene's materials, lighting, exposure, camera language and motion rhythm.
// ---------------------------------------------------------------------------

export const MATS = {
  GOLD: { color: "#E5C07B", metalness: 0.9, roughness: 0.3 },
  GOLD_SOFT: { color: "#D9B36A", metalness: 0.8, roughness: 0.34, emissive: "#7a5a24", emissiveIntensity: 0.18 },
  DARK: { color: "#332a21", roughness: 0.85 },
  DARKER: { color: "#241e17", roughness: 0.9 },
  POOL: { color: "#175268", metalness: 0.9, roughness: 0.1, emissive: "#0f3d4e", emissiveIntensity: 1.0 },
};

// Same tone-mapping exposure as the main cinematic scene
export const TONE_EXPOSURE = 1.35;

// Warm interior window strip — shared accent across all property objects
export function Strip({ position, w = 0.5, h = 0.1 }) {
  return (
    <mesh position={position}>
      <boxGeometry args={[w, h, 0.03]} />
      <meshStandardMaterial color="#2a1a0c" emissive="#e8a558" emissiveIntensity={1.85} />
    </mesh>
  );
}

// Unified lighting rig mirroring the main scene:
// cool moonlight from the upper left + champagne key light + bronze fill.
export function StageLights({ boost = 1 }) {
  return (
    <>
      <ambientLight intensity={0.5} />
      <directionalLight position={[-4, 6, 4]} intensity={0.85 * boost} color="#c8d4ec" />
      <pointLight position={[2.5, 2.5, 3]} color="#F3E5AB" intensity={3.2 * boost} />
      <pointLight position={[-2.5, 1, 2]} color="#CD9551" intensity={1.2 * boost} />
    </>
  );
}

function CalmSpin({ children }) {
  const ref = useRef();
  useFrame(({ clock }) => {
    if (ref.current) ref.current.rotation.y = Math.sin(clock.elapsedTime * 0.14) * 0.55;
  });
  return <group ref={ref}>{children}</group>;
}

// One calm motion rhythm across all 3D objects — slow drift, no fast spins
export function CalmMotion({ children, scale = 1 }) {
  return (
    <Float speed={1.2} rotationIntensity={0.15} floatIntensity={0.35}>
      <CalmSpin>
        <group scale={scale}>{children}</group>
      </CalmSpin>
    </Float>
  );
}

// Shared stage canvas — same exposure, dpr and camera language everywhere
export function StageCanvas({ children, camera = { position: [0, 0.5, 3.6], fov: 40 } }) {
  return (
    <Canvas
      dpr={[1, 1.5]}
      camera={camera}
      gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      onCreated={({ gl }) => {
        gl.toneMappingExposure = TONE_EXPOSURE;
      }}
      style={{ background: "transparent", width: "100%", height: "100%" }}
    >
      {children}
    </Canvas>
  );
}

// Lazy-mount helper — resolves true once the target nears the viewport,
// so WebGL contexts are only created when a section is about to be seen.
export function useInViewMount(ref, rootMargin = "400px") {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShow(true);
          obs.disconnect();
        }
      },
      { rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, rootMargin, show]);
  return show;
}

// Real-architectural-photography helpers -------------------------------------

// Loads a photo texture with sRGB colour and cover-crops it to the target
// plane aspect (no distortion, centre-cropped like a magazine frame).
export function usePhotoTexture(url, aspect) {
  const tex = useLoader(THREE.TextureLoader, url);
  useMemo(() => {
    if (!tex.image) return;
    tex.colorSpace = THREE.SRGBColorSpace;
    const a = tex.image.width / tex.image.height;
    if (a > aspect) {
      tex.repeat.set(aspect / a, 1);
      tex.offset.set((1 - aspect / a) / 2, 0);
    } else {
      tex.repeat.set(1, a / aspect);
      tex.offset.set(0, (1 - a / aspect) / 2);
    }
    tex.needsUpdate = true;
  }, [tex, aspect]);
  return tex;
}

// A framed luxury-home photograph for the satellite stages — slim champagne
// gallery frame, drop-in replacement for the old procedural house models.
export function PhotoPlane({ url, w = 1.8, h = 1.2, position = [0, 0, 0], frame = true }) {
  const tex = usePhotoTexture(url, w / h);
  return (
    <group position={position}>
      <mesh>
        <planeGeometry args={[w, h]} />
        <meshStandardMaterial map={tex} side={THREE.DoubleSide} roughness={0.92} metalness={0.04} />
      </mesh>
      {frame && (
        <>
          <mesh position={[0, h / 2 + 0.02, 0.004]}>
            <boxGeometry args={[w + 0.08, 0.04, 0.02]} />
            <meshStandardMaterial {...MATS.GOLD} />
          </mesh>
          <mesh position={[0, -h / 2 - 0.02, 0.004]}>
            <boxGeometry args={[w + 0.08, 0.04, 0.02]} />
            <meshStandardMaterial {...MATS.GOLD} />
          </mesh>
          <mesh position={[-w / 2 - 0.02, 0, 0.004]}>
            <boxGeometry args={[0.04, h + 0.08, 0.02]} />
            <meshStandardMaterial {...MATS.GOLD} />
          </mesh>
          <mesh position={[w / 2 + 0.02, 0, 0.004]}>
            <boxGeometry args={[0.04, h + 0.08, 0.02]} />
            <meshStandardMaterial {...MATS.GOLD} />
          </mesh>
        </>
      )}
    </group>
  );
}
